import { Access, FeatureType, ProgressStatus, Resources, Role } from 'src/shared/types';
import type { UserProjectAccess } from 'src/app/admin/views/user-management/state/userManagementSlice';
import type { CustomerProject, UserListSummaryRow } from 'src/app/projects/views/project-list/state/api/queries/projectsListQuery';

/** Sites shown per page in the assign-permissions matrix table */
export const MATRIX_SITES_PAGE_SIZE = 10;

/** Bulk assign/unassign is shown only when manageable site count is greater than this (e.g. more than one matrix page). */
export const MATRIX_BULK_ACTION_MIN_TOTAL = 10;

/** Per-role cell: checkbox plus optional user counts / limits from `userCountSummary`. */
export type MatrixCountByRole = {
    name: string;
    checked: boolean;
    roleUserCount?: number;
    roleUserLimitMax?: number;
    roleUserLimitSoft?: number | null;
    /**
     * False when `userCountSummary` returned data for this project but omitted
     * this role from `countByRoles`, meaning the subscription does not include it.
     * When false the cell should be hidden (no checkbox) and bulk actions must skip it.
     * Defaults to `true` when summary data has not loaded yet.
     */
    roleAvailable?: boolean;
};

/** projectId → role name → counts/limits (from `userCountSummary`). */
export type RoleUserStatsByProject = Record<string, Partial<Record<string, { roleCount: number; max: number; softLimit?: number | null }>>>;

export function userCountSummaryToRoleStatsByProject(summaries: UserListSummaryRow[] | undefined): RoleUserStatsByProject | undefined {
    if (!summaries?.length) {
        return undefined;
    }
    const out: RoleUserStatsByProject = {};
    for (const s of summaries) {
        if (!s.projectId) {
            continue;
        }
        const perRole: Partial<Record<string, { roleCount: number; max: number; softLimit?: number | null }>> = {};
        for (const c of s.countByRoles) {
            if (c.name === null || c.name === undefined || c.name === '') {
                continue;
            }
            const key = String(c.name);
            perRole[key] = {
                roleCount: c.roleCount ?? 0,
                max: c.limitation.max,
                softLimit: c.limitation.softLimit,
            };
        }
        out[s.projectId] = perRole;
    }
    return out;
}

/** True when another user cannot be given this role (seat cap), based on API counts. */
export function isRoleAssignmentAtCapacity(entry: MatrixCountByRole): boolean {
    if (entry.checked) {
        return false;
    }
    const count = entry.roleUserCount;
    const max = entry.roleUserLimitMax;
    if (count === null || count === undefined || max === null || max === undefined) {
        return false;
    }
    return count >= max;
}

/** Cell background hint: `error` at hard cap (cannot assign), `warning` at/above soft limit. */
export function matrixCellHighlightClass(entry: MatrixCountByRole): 'error' | 'warning' | '' {
    if (isRoleAssignmentAtCapacity(entry)) {
        return 'error';
    }
    if (
        entry.roleUserLimitSoft !== null &&
        entry.roleUserLimitSoft !== undefined &&
        entry.roleUserCount !== null &&
        entry.roleUserCount !== undefined &&
        entry.roleUserCount >= entry.roleUserLimitSoft
    ) {
        return 'warning';
    }
    return '';
}

/** Single site row in the matrix UI */
export type MatrixRowState = {
    projectId: string;
    projectName: string;
    assignable: boolean;
    countByRoles: MatrixCountByRole[];
    /** Carried through for upserts when the form already has a remark */
    remark: string;
};

/** Abstraction over `usePermissions().hasPermission` for pure helpers/tests */
export type HasPermissionFn = (args: { projectId: string; to: Access; resource: Resources }) => boolean;

/** Site appears in the assignable catalogue when complete (or survey feature) and caller may assign users */
export function isProjectInAssignableList(project: CustomerProject, hasPermission: HasPermissionFn): boolean {
    const canAssignToThisProject = hasPermission({
        projectId: project.projectId,
        to: Access.CREATE,
        resource: Resources.USER,
    });
    if (!canAssignToThisProject) {
        return false;
    }
    return [project.status].includes(ProgressStatus.COMPLETED) || Boolean(project?.features?.includes(FeatureType.SURVEY));
}

/** Stable role column order for the matrix */
const ROLE_SORT_ORDER: string[] = [
    Role.PROJECT_ADMIN,
    Role.PROJECT_VIEWER,
    Role.NM_PROJECT_ADMIN,
    Role.NM_CUSTOMER_MANAGER,
    Role.ADMIN,
    Role.CUSTOMER_ADMIN,
];

/** Known roles first (see ROLE_SORT_ORDER), then any other names, alphabetically. */
export function sortRoleNames(names: string[]): string[] {
    const set = new Set(names);
    const ordered = ROLE_SORT_ORDER.filter(r => set.has(r));
    const rest = [...names].filter(n => !ROLE_SORT_ORDER.includes(n)).sort();
    return [...ordered, ...rest];
}

/**
 * Column headers for the matrix: API role list (subscription limitations union) plus any role names
 * already on the form so columns stay stable while editing.
 */
export function mergeMatrixRoleColumnNames(apiRoles: string[], projects: UserProjectAccess[]): string[] {
    const names = new Set<string>();
    apiRoles.forEach(r => {
        if (r) {
            names.add(r);
        }
    });
    projects.forEach(p =>
        (p.roles ?? []).forEach(r => {
            if (r) {
                names.add(r);
            }
        })
    );
    return sortRoleNames([...names]);
}

/** Fallback when the roles API is unavailable: known order plus form roles. */
export function collectMatrixRoleNamesFromProjects(projects: UserProjectAccess[]): string[] {
    return mergeMatrixRoleColumnNames([...ROLE_SORT_ORDER], projects);
}

/**
 * Hides role columns per policy (e.g. internal users only see viewer-like roles;
 * project admins may not see Project Admin column unless they are customer admin).
 * Super admins bypass the internal-user viewer-only restriction so they see the full matrix.
 */
export function roleVisibleInMatrix(
    roleName: string,
    options: {
        isInternalUser: boolean;
        isCurrentUserCustomerAdmin: boolean;
        isCurrentUserProjectAdmin: boolean;
        /** When true, skip hiding non-viewer columns for internal assignees (super admin full matrix). */
        isActorSuperAdmin?: boolean;
    }
): boolean {
    const { isInternalUser, isCurrentUserCustomerAdmin, isCurrentUserProjectAdmin } = options;
    if (options.isActorSuperAdmin) {
        return true;
    }

    if (!isCurrentUserCustomerAdmin && roleName === Role.PROJECT_ADMIN && isCurrentUserProjectAdmin) {
        return false;
    }
    if (isInternalUser && !roleName.toLowerCase().includes('viewer')) {
        return false;
    }
    return true;
}

/** Matches `usePermissions`’s `hasProjectLevelRole` callback shape */
export type HasProjectLevelRoleFn = (params: { projectId: string; role: Role | Role[] }) => boolean;

/**
 * True when the actor has Project Admin on every site in the current customer.
 * Used for matrix *column* visibility (not per-cell), together with `filterVisibleMatrixRoleColumns`.
 */
export function actorIsProjectAdminOnAllCustomerSites(
    customerProjectIds: string[] | undefined,
    hasProjectLevelRole: HasProjectLevelRoleFn
): boolean {
    if (!customerProjectIds?.length) {
        return false;
    }
    return customerProjectIds.every(projectId => hasProjectLevelRole({ projectId, role: Role.PROJECT_ADMIN }));
}

/**
 * Columns to show in the site × role matrix for the current actor. Prefer this over filtering with a
 * paginated row list: the editable matrix only loads one page of sites at a time, which previously hid
 * role columns when every row on that page was a site where the actor is Project Admin.
 */
export function filterVisibleMatrixRoleColumns(
    columnRoles: string[],
    options: {
        isInternalUser: boolean;
        isCurrentUserCustomerAdmin: boolean;
        isCurrentUserSuperAdmin: boolean;
        customerProjectIds: string[];
        hasProjectLevelRole: HasProjectLevelRoleFn;
    }
): string[] {
    const { isInternalUser, isCurrentUserCustomerAdmin, isCurrentUserSuperAdmin, customerProjectIds, hasProjectLevelRole } = options;

    const hideProjectAdminColumnForActor =
        !isCurrentUserCustomerAdmin && actorIsProjectAdminOnAllCustomerSites(customerProjectIds, hasProjectLevelRole);

    return columnRoles.filter(roleName =>
        roleVisibleInMatrix(roleName, {
            isInternalUser,
            isCurrentUserCustomerAdmin,
            isCurrentUserProjectAdmin: hideProjectAdminColumnForActor,
            isActorSuperAdmin: isCurrentUserSuperAdmin,
        })
    );
}

/** True when the role is not available for this project (subscription does not include it). */
export function isRoleUnavailableForProject(entry: MatrixCountByRole): boolean {
    return entry.roleAvailable === false;
}

/** True when matrix is read-only or role hidden by admin/PA rules */
export function isCellCheckboxDisabled(
    entry: MatrixCountByRole,
    options: {
        disabled: boolean;
        formMode: Access;
        isCurrentUserCustomerAdmin: boolean;
        isCurrentUserProjectAdmin: boolean;
    }
): boolean {
    const { disabled, formMode, isCurrentUserCustomerAdmin, isCurrentUserProjectAdmin } = options;
    if (disabled || formMode === Access.VIEW) {
        return true;
    }
    if (isRoleUnavailableForProject(entry)) {
        return true;
    }
    if (isRoleAssignmentAtCapacity(entry)) {
        return true;
    }
    return !isCurrentUserCustomerAdmin && entry.name === Role.PROJECT_ADMIN && isCurrentUserProjectAdmin;
}

/** Serialises checked cells back into `formState.projects` rows */
export function buildProjectsFromRows(rows: MatrixRowState[]): UserProjectAccess[] {
    return rows
        .filter(row => row.assignable && row.countByRoles.some(c => c.checked))
        .map(row => ({
            projectId: row.projectId,
            roles: row.countByRoles.filter(c => c.checked).map(c => c.name),
            remark: row.remark || '',
        }));
}

/** Merge a single API page of matrix rows into full form `projects` (other pages unchanged). */
export function mergeProjectsAfterMatrixPageSync(
    prevProjects: UserProjectAccess[],
    pageProjectIds: string[],
    fromPage: UserProjectAccess[]
): UserProjectAccess[] {
    const pageSet = new Set(pageProjectIds.filter(Boolean));
    const withoutPage = prevProjects.filter(p => !p.projectId || !pageSet.has(p.projectId));
    return [...withoutPage, ...fromPage];
}

/** Create flow needs at least one site role (or org roles handled elsewhere) */
export function hasAtLeastOneAssignedRole(rows: MatrixRowState[]): boolean {
    return rows.some(row => row.assignable && row.countByRoles.some(c => c.checked));
}

/** Single gate for parent form `disableSubmit` from matrix-derived rules */
export function aggregateDisableSubmit(rows: MatrixRowState[], formMode: Access): boolean {
    if (formMode === Access.CREATE && !hasAtLeastOneAssignedRole(rows)) {
        return true;
    }
    return false;
}

/**
 * Matrix rows = every site in `projectsData` for this customer, in the same order as `projectsData`
 * (server `usersQueries.userManagementProjectList` orders by project name; do not re-sort by projectId here).
 * `assignableProjectIds` controls per-row edit eligibility (view-only when false).
 */
export function buildMatrixRowsFromProjectList(
    projectsData: CustomerProject[],
    projects: UserProjectAccess[],
    assignableProjectIds: Set<string>,
    matrixRoleColumnNames: string[],
    roleStatsByProject?: RoleUserStatsByProject
): MatrixRowState[] {
    const roleNames = matrixRoleColumnNames;
    const projectById = new Map(projectsData.map(p => [p.projectId, p]));

    const orderedProjectIds: string[] = [];
    const seen = new Set<string>();
    for (const p of projectsData) {
        if (!p.projectId || seen.has(p.projectId)) {
            continue;
        }
        seen.add(p.projectId);
        orderedProjectIds.push(p.projectId);
    }

    return orderedProjectIds.map(projectId => {
        const meta = projectById.get(projectId);
        const formEntry = projects.find(p => p.projectId === projectId);
        const assignable = assignableProjectIds.has(projectId);
        const statsForProject = roleStatsByProject?.[projectId];

        const summaryLoadedForProject = statsForProject !== undefined;

        const countByRoles: MatrixCountByRole[] = roleNames.map(name => {
            const stats = statsForProject?.[name];
            const roleAvailable = summaryLoadedForProject ? stats !== undefined : true;
            return {
                name,
                checked: Boolean(formEntry?.roles?.includes(name)),
                roleUserCount: stats?.roleCount,
                roleUserLimitMax: stats?.max,
                roleUserLimitSoft: stats?.softLimit,
                roleAvailable,
            };
        });

        return {
            projectId,
            projectName: meta?.projectName || projectId,
            assignable,
            countByRoles,
            remark: formEntry?.remark ?? '',
        };
    });
}

/** Applies server `bulkApplyUserManagementSiteRole` result to the full form `projects` list. */
export function applyBulkSiteRoleResultToProjects(
    prev: UserProjectAccess[],
    changedProjectIds: string[],
    roleName: string,
    assign: boolean
): UserProjectAccess[] {
    let next = [...prev];
    for (const pid of changedProjectIds) {
        const idx = next.findIndex(p => p.projectId === pid);
        const existing = idx >= 0 ? next[idx] : undefined;
        const roleSet = new Set<string>(existing?.roles ?? []);
        if (assign) {
            roleSet.add(roleName);
        } else {
            roleSet.delete(roleName);
        }
        const newRoles = sortRoleNames([...roleSet]) as Role[];
        if (newRoles.length === 0) {
            if (idx >= 0) {
                next = [...next.slice(0, idx), ...next.slice(idx + 1)];
            }
        } else {
            const row: UserProjectAccess = {
                projectId: pid,
                roles: newRoles,
                remark: existing?.remark ?? '',
            };
            if (idx >= 0) {
                next = [...next.slice(0, idx), row, ...next.slice(idx + 1)];
            } else {
                next = [...next, row];
            }
        }
    }
    return next;
}

/** Immutable row update: flip one checkbox */
export function toggleMatrixRowRoleChecked(
    rows: MatrixRowState[],
    projectId: string,
    roleName: string,
    nextChecked: boolean
): MatrixRowState[] {
    return rows.map(row => {
        if (row.projectId !== projectId) {
            return row;
        }
        return {
            ...row,
            countByRoles: row.countByRoles.map(c => (c.name === roleName ? { ...c, checked: nextChecked } : c)),
        };
    });
}

/** Shared permission context for bulk assign/unassign simulation (mirrors per-cell rules) */
export type ApplyRoleBulkContext = {
    disabled: boolean;
    formMode: Access;
    isInternalUser: boolean;
    isCurrentUserCustomerAdmin: boolean;
    isProjectAdminFor: (projectId: string) => boolean;
    isActorSuperAdmin?: boolean;
};

export type BulkTurnOnRoleResult = {
    rows: MatrixRowState[];
    appliedCount: number;
    skippedAlreadyHad: number;
    skippedIneligible: number;
};

/**
 * Turn on one role for every row where the matrix would allow checking that cell (skips hidden / disabled).
 */
export function bulkTurnOnRoleForEligibleRows(rows: MatrixRowState[], roleName: string, ctx: ApplyRoleBulkContext): BulkTurnOnRoleResult {
    let next = rows;
    let appliedCount = 0;
    let skippedAlreadyHad = 0;
    let skippedIneligible = 0;

    for (const row of rows) {
        if (!row.assignable) {
            skippedIneligible += 1;
            continue;
        }
        const entry = row.countByRoles.find(c => c.name === roleName);
        if (!entry) {
            skippedIneligible += 1;
            continue;
        }
        const isPA = ctx.isProjectAdminFor(row.projectId);
        if (
            !roleVisibleInMatrix(roleName, {
                isInternalUser: ctx.isInternalUser,
                isCurrentUserCustomerAdmin: ctx.isCurrentUserCustomerAdmin,
                isCurrentUserProjectAdmin: isPA,
                isActorSuperAdmin: ctx.isActorSuperAdmin,
            })
        ) {
            skippedIneligible += 1;
            continue;
        }
        if (
            isCellCheckboxDisabled(entry, {
                disabled: ctx.disabled,
                formMode: ctx.formMode,
                isCurrentUserCustomerAdmin: ctx.isCurrentUserCustomerAdmin,
                isCurrentUserProjectAdmin: isPA,
            })
        ) {
            skippedIneligible += 1;
            continue;
        }
        if (entry.checked) {
            skippedAlreadyHad += 1;
            continue;
        }
        next = toggleMatrixRowRoleChecked(next, row.projectId, roleName, true);
        appliedCount += 1;
    }

    return { rows: next, appliedCount, skippedAlreadyHad, skippedIneligible };
}

export type BulkTurnOffRoleResult = {
    rows: MatrixRowState[];
    removedCount: number;
    skippedNotHad: number;
    skippedIneligible: number;
};

/**
 * Turn off one role for every assignable row where the cell is checked and the matrix would allow unchecking (matches column header “select all” off).
 */
export function bulkTurnOffRoleForEligibleRows(rows: MatrixRowState[], roleName: string, ctx: ApplyRoleBulkContext): BulkTurnOffRoleResult {
    let next = rows;
    let removedCount = 0;
    let skippedNotHad = 0;
    let skippedIneligible = 0;

    for (const row of rows) {
        if (!row.assignable) {
            skippedIneligible += 1;
            continue;
        }
        const entry = row.countByRoles.find(c => c.name === roleName);
        if (!entry) {
            skippedIneligible += 1;
            continue;
        }
        const isPA = ctx.isProjectAdminFor(row.projectId);
        if (!entry.checked) {
            skippedNotHad += 1;
            continue;
        }
        if (
            isCellCheckboxDisabled(entry, {
                disabled: ctx.disabled,
                formMode: ctx.formMode,
                isCurrentUserCustomerAdmin: ctx.isCurrentUserCustomerAdmin,
                isCurrentUserProjectAdmin: isPA,
            })
        ) {
            skippedIneligible += 1;
            continue;
        }
        next = toggleMatrixRowRoleChecked(next, row.projectId, roleName, false);
        removedCount += 1;
    }

    return { rows: next, removedCount, skippedNotHad, skippedIneligible };
}
